import { forwardRef, useImperativeHandle, useRef } from 'react';
import PanelBoxLayout from 'components/layout/panelBox';
import { Formik } from 'formik';
import Gap from 'components/gap';
import DateInput from 'components/form/dateInput';
import TextInput from 'components/form/textInput';
import { ValidationCreateSurvey } from 'common/validations';
import { useActions } from 'common/hooks/useActions';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'common/helpers/toastConfig';
import dayjs from 'dayjs';
import * as S from './styles';

interface IProps {
  questions: any[];
  data?: any;
}

const SurveyCreateFormInfo = forwardRef(({ questions, data }: IProps, ref) => {
  const { surveyUpdateRequestAction, surveyCreateRequestAction } = useActions();
  const navigate = useNavigate();
  const params = useParams();

  const formRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    }
  }));

  const transformData = (input: any) => {
    function cleanObject(obj: any): any {
      if (Array.isArray(obj)) {
        return obj.map(cleanObject);
      } else if (typeof obj === 'object' && obj !== null) {
        let newObj: Record<string, any> = {};
        for (let key in obj) {
          if (key !== 'id' && key !== 'typeTitle') {
            newObj[key] = cleanObject(obj[key]);
          }
        }
        return newObj;
      }
      return obj;
    }
    return cleanObject(input);
  };

  return (
    <S.Content>
      <PanelBoxLayout style={{ padding: '20px 30px' }}>
        <S.Head>Survey Details</S.Head>
        <Gap size="30px" />
        <S.Detail>
          <Formik
            innerRef={formRef}
            initialValues={{
              startsAt: data?.startsAt ?? '',
              endsAt: data?.endsAt ?? '',
              en_title: data?.surveyTranslations?.find((t: any) => t.language === 'en')?.title ?? '',
              ar_title: data?.surveyTranslations?.find((t: any) => t.language === 'ar')?.title ?? ''
            }}
            enableReinitialize
            validationSchema={ValidationCreateSurvey}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              if (new Date(values?.startsAt) > new Date(values?.endsAt)) {
                toast({
                  model: 'error',
                  message: 'The end date cannot be smaller than the start date'
                });
                return;
              }
              if (!questions.length) {
                toast({ model: 'error', message: 'Please enter a question' });
                return;
              }
              const requestBody = {
                translations: [
                  { language: 'en', title: values.en_title },
                  { language: 'ar', title: values.ar_title }
                ],
                startsAt: values.startsAt,
                endsAt: values.endsAt,
                questions: transformData(questions),
                ...(params.id ? { id: +params.id } : {})
              };
              params.id
                ? surveyUpdateRequestAction(requestBody, () => navigate('/panel/survey'))
                : surveyCreateRequestAction(requestBody, () => navigate('/panel/survey'));
            }}
          >
            {({ values, errors, touched, handleSubmit, setValues }) => (
              <>
                <div className="w-100 d-flex align-items-center gap-4">
                  <TextInput
                    label="English Title"
                    name="en_title"
                    value={values.en_title}
                    onChange={(e) => setValues({ ...values, en_title: e.target.value })}
                    placeholder="Please Enter Survey Title"
                    message={touched.en_title && (errors.en_title as any)}
                    error={errors.en_title as unknown as boolean}
                  />

                  <TextInput
                    label="Arabic Title"
                    name="ar_title"
                    value={values.ar_title}
                    onChange={(e) => setValues({ ...values, ar_title: e.target.value })}
                    placeholder="يرجى إدخال عنوان الاستطلاع"
                    message={touched.ar_title && (errors.ar_title as any)}
                    error={errors.ar_title as unknown as boolean}
                  />
                </div>

                <Gap size="20px" />

                <div className="w-100 d-flex align-items-center gap-4">
                  <DateInput
                    onChange={(date) => setValues({ ...values, startsAt: date || '' })}
                    label="Start Date"
                    defaultDate={values.startsAt ? dayjs(values.startsAt) : ''}
                    format="YYYY/MM/DD"
                    placeholder="From"
                    message={touched.startsAt && (errors.startsAt as any)}
                    error={touched.startsAt && (errors.startsAt as any)}
                  />

                  <DateInput
                    onChange={(date) => setValues({ ...values, endsAt: date || '' })}
                    placeholder="To"
                    defaultDate={values.endsAt ? dayjs(values.endsAt) : ''}
                    format="YYYY/MM/DD"
                    label="End Date"
                    message={touched.endsAt && (errors.endsAt as any)}
                    error={touched.endsAt && (errors.endsAt as any)}
                  />
                </div>
              </>
            )}
          </Formik>
        </S.Detail>
        <Gap size="20px" />
      </PanelBoxLayout>
    </S.Content>
  );
});

export default SurveyCreateFormInfo;
